import { FormEvent, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { PublicLayout } from '../components/layout/PublicLayout'
import { EventTypePill } from '../components/ui/EventTypePill'
import { AvatarStack } from '../components/ui/Avatar'
import { EVENT_TYPES, EventTypeKey } from '../components/ui/eventTypes'
import { useIsMobile } from '../hooks/useIsMobile'
import { MapPin, Calendar, Clock, Search, ChevronRight, Building2, User, ArrowRight } from 'lucide-react'
import type { EventType } from '@fellowship/shared'

const PREVIEW_EVENTS_BASE = [
  { id: 1, type: 'potluck', name: 'Church fellowship lunch', time: '12:45 pm', host: 'Riverside Community Church', hostedByPerson: false, dist: '0.8 mi', going: 28, recurring: true, image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=600&h=280&q=80' },
  { id: 2, type: 'outdoors', name: 'Sunrise hike, Box Springs', time: '6:30 am', host: 'Marcus T.', hostedByPerson: true, dist: '2.1 mi', going: 12, recurring: false, image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=600&h=280&q=80' },
  { id: 3, type: 'study', name: 'Wednesday evening study', time: '7:00 pm', host: 'Loma Linda Central SDA', hostedByPerson: false, dist: '4.4 mi', going: 9, recurring: true, image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=600&h=280&q=80' },
]

function getWeekendDates(): { sat: Date; sun: Date; wed: Date } {
  const today = new Date()
  const day = today.getDay() // 0=Sun...6=Sat
  const sat = new Date(today)
  if (day === 0) {
    sat.setDate(today.getDate() - 1) // Sunday → use yesterday's Sat
  } else {
    sat.setDate(today.getDate() + (6 - day))
  }
  const sun = new Date(sat)
  sun.setDate(sat.getDate() + 1)
  const wed = new Date(sun)
  wed.setDate(sun.getDate() + 3)
  return { sat, sun, wed }
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function isoDate(d: Date): string {
  return d.toISOString().split('T')[0]
}

const SAMPLE_NAMES = ['Sarah M.', 'David K.', 'Rachel T.', 'James O.', 'Maria C.', 'Chris R.']

const GRADIENT = 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)'

type PreviewEvent = typeof PREVIEW_EVENTS_BASE[0] & { date: string }

function EventCard({ e, rotate }: { e: PreviewEvent; rotate: string }) {
  const t = EVENT_TYPES[e.type as EventTypeKey]
  const HostIcon = e.hostedByPerson ? User : Building2
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #ece5d6',
      borderRadius: 16,
      padding: '18px 20px',
      transform: rotate,
      boxShadow: '0 4px 20px rgba(31,26,46,.12)',
      userSelect: 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <EventTypePill type={e.type} size="sm" />
        {e.recurring && (
          <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', background: 'var(--canvas)', padding: '2px 8px', borderRadius: 999, border: '1px solid var(--line)' }}>
            weekly
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted)' }}>{e.dist}</span>
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.25, color: 'var(--ink)', marginBottom: 8, letterSpacing: '-0.01em' }}>
        {e.name}
      </div>
      <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <Calendar size={11} color={t.color} /> {e.date}
        </span>
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <Clock size={11} color={t.color} /> {e.time}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <HostIcon size={11} color="var(--muted)" />
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{e.host}</span>
        </div>
        <AvatarStack names={SAMPLE_NAMES.slice(0, 3)} size={18} />
      </div>
    </div>
  )
}

export default function HomePage() {
  const { user, accessToken } = useAuthStore()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [location, setLocation] = useState(user?.locationLabel ?? '')
  const { sat, sun, wed } = getWeekendDates()
  const PREVIEW_EVENTS: PreviewEvent[] = PREVIEW_EVENTS_BASE.map((e, i) => ({
    ...e,
    date: fmtDate([sat, sun, wed][i]),
  }))

  function handleSearch(e: FormEvent) {
    e.preventDefault()
    if (!location.trim()) return
    navigate(`/search?q=${encodeURIComponent(location.trim())}&from=${isoDate(sat)}&to=${isoDate(sun)}`)
  }

  function navigateToType(type: EventType | 'all') {
    if (type === 'all') navigate('/search')
    else navigate(`/search?eventTypes=${type}`)
  }

  return (
    <PublicLayout activeNav="Discover">
      {/* Hero */}
      <section style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Gradient blobs */}
        <div style={{ position: 'absolute', top: -160, right: -100, width: 520, height: 520, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,92,211,.18) 0%, transparent 65%)', filter: 'blur(24px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 120, left: -180, width: 560, height: 560, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,76,202,.14) 0%, transparent 65%)', filter: 'blur(24px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', maxWidth: 1240, margin: '0 auto', padding: isMobile ? '40px 20px 48px' : '80px 36px 100px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 60, alignItems: 'center' }}>
          {/* Left */}
          <div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, padding: '7px 14px', background: '#fff', border: '1px solid var(--line)', borderRadius: 999, color: 'var(--ink-2)', boxShadow: '0 1px 8px rgba(31,26,46,.06)', marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              Interdenominational · 240+ churches
            </span>

            <h1 style={{ fontSize: isMobile ? 42 : 72, lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 600, margin: '0 0 24px', color: 'var(--ink)' }}>
              Come as you are.<br />
              <span className="serif-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Find your people.
              </span>
            </h1>

            <p style={{ fontSize: isMobile ? 16 : 18, lineHeight: 1.6, color: 'var(--ink-2)', margin: '0 0 36px', maxWidth: 480 }}>
              Potlucks, hikes, studies, and service days hosted by churches and individuals near you. One feed across every denomination — pick what feels like home.
            </p>

            {/* Search bar */}
            {isMobile ? (
              <form onSubmit={handleSearch} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ background: '#fff', borderRadius: 16, padding: '12px 16px', border: '1px solid var(--line-soft)', boxShadow: '0 2px 12px rgba(31,26,46,.06)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>WHERE</div>
                  <input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="City or address"
                    aria-label="Search location"
                    style={{ border: 0, background: 'transparent', outline: 'none', fontSize: 15, fontWeight: 500, color: 'var(--ink)', width: '100%', fontFamily: 'inherit' }}
                  />
                </div>
                <button
                  type="submit"
                  style={{ height: 50, borderRadius: 999, border: 'none', background: GRADIENT, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 12px rgba(59,76,202,.4)', fontFamily: 'inherit' }}
                >
                  <Search size={15} /> Find events near me
                </button>
              </form>
            ) : (
              <form
                onSubmit={handleSearch}
                style={{ background: '#fff', borderRadius: 999, padding: 6, boxShadow: '0 1px 2px rgba(31,26,46,.05), 0 18px 40px rgba(31,26,46,.07)', display: 'flex', alignItems: 'center', maxWidth: 560 }}
              >
                <div style={{ flex: 1.4, padding: '8px 22px', borderRight: '1px solid var(--line-soft)' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>WHERE</div>
                  <input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="City or address"
                    aria-label="Search location"
                    style={{ border: 0, background: 'transparent', outline: 'none', fontSize: 14.5, fontWeight: 500, color: 'var(--ink)', width: '100%', fontFamily: 'inherit' }}
                  />
                </div>
                <div style={{ flex: 1, padding: '8px 22px' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>WHEN</div>
                  <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>This weekend</div>
                </div>
                <button
                  type="submit"
                  style={{ height: 52, padding: '0 28px', borderRadius: 999, border: 'none', background: GRADIENT, color: '#fff', fontSize: 14.5, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(59,76,202,.4)', fontFamily: 'inherit', flexShrink: 0 }}
                >
                  <Search size={15} /> Search
                </button>
              </form>
            )}

            {/* Social proof */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 28 }}>
              <AvatarStack names={SAMPLE_NAMES} size={32} max={5} />
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.4 }}>
                <strong style={{ color: 'var(--ink)' }}>2,400+</strong> neighbors finding community each week
              </p>
            </div>

            {!accessToken && (
              <p style={{ marginTop: 18, fontSize: 13, color: 'var(--muted)' }}>
                <Link to="/register" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
                  Create an account
                </Link>{' '}
                to RSVP and host your own events.
              </p>
            )}
          </div>

          {/* Right — stacked rotated preview cards (desktop only) */}
          {!isMobile && (
            <div style={{ position: 'relative', minHeight: 500 }}>
              <div style={{ position: 'absolute', top: 0, right: 0, width: 300 }}>
                <EventCard e={PREVIEW_EVENTS[0]} rotate="rotate(-2.2deg)" />
              </div>
              <div style={{ position: 'absolute', top: 160, left: 0, width: 285 }}>
                <EventCard e={PREVIEW_EVENTS[1]} rotate="rotate(1.6deg)" />
              </div>
              <div style={{ position: 'absolute', top: 320, right: 20, width: 275 }}>
                <EventCard e={PREVIEW_EVENTS[2]} rotate="rotate(-1deg)" />
              </div>
              {/* Floating denomination-aware stamp pill */}
              <div style={{
                position: 'absolute',
                bottom: 10,
                left: 20,
                background: '#fff',
                borderRadius: 999,
                padding: '8px 16px',
                boxShadow: '0 4px 16px rgba(31,26,46,.12)',
                border: '1px solid var(--line)',
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--ink)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                {user?.denomination === 'seventh-day-adventist' ? 'THIS SABBATH' : 'THIS WEEKEND'}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Event type grid */}
      <section style={{ maxWidth: 1240, margin: '0 auto', padding: isMobile ? '0 20px 48px' : '0 36px 80px' }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', margin: '0 0 20px' }}>
          What sounds like you?
        </h2>
        <div style={isMobile ? { display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' } : { display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 12 }}>
          {/* All types */}
          <button
            onClick={() => navigateToType('all')}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
              padding: '20px 12px', background: '#fff', border: '1px solid var(--line)',
              borderRadius: 16, cursor: 'pointer', textAlign: 'center', fontFamily: 'inherit',
              flexShrink: isMobile ? 0 : undefined, minWidth: isMobile ? 80 : undefined,
            }}
          >
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span aria-hidden="true" style={{ fontSize: 16 }}>✦</span>
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.25 }}>All</div>
          </button>
          {(Object.entries(EVENT_TYPES) as [EventTypeKey, typeof EVENT_TYPES[EventTypeKey]][]).map(([id, t]) => (
            <button
              key={id}
              onClick={() => navigateToType(id as EventType)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                padding: '20px 12px',
                background: '#fff',
                border: '1px solid var(--line)',
                borderRadius: 16,
                cursor: 'pointer',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                textAlign: 'center',
                fontFamily: 'inherit',
                flexShrink: isMobile ? 0 : undefined,
                minWidth: isMobile ? 80 : undefined,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.03)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(31,26,46,.08)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; (e.currentTarget as HTMLButtonElement).style.boxShadow = '' }}
            >
              <div style={{ width: 38, height: 38, borderRadius: 10, background: t.tint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ width: 14, height: 14, borderRadius: '50%', background: t.color, display: 'inline-block' }} />
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.25 }}>{t.label}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Featured this week */}
      <section style={{ maxWidth: 1240, margin: '0 auto', padding: isMobile ? '0 20px 48px' : '0 36px 80px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 22 : 28, fontWeight: 600, letterSpacing: '-0.015em', color: 'var(--ink)' }}>
            Featured this week
          </h2>
          <Link to="/search" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: 'var(--ink-2)', textDecoration: 'none', fontWeight: 500 }}>
            See all <ChevronRight size={14} />
          </Link>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 4, marginBottom: 16, scrollbarWidth: 'none' }}>
            {PREVIEW_EVENTS.map(e => {
              const t = EVENT_TYPES[e.type as EventTypeKey]
              return (
                <Link key={e.id} to="/search" style={{ textDecoration: 'none', flexShrink: 0, width: 240 }}>
                  <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 20, overflow: 'hidden', boxShadow: '0 1px 2px rgba(31,26,46,.04), 0 12px 28px rgba(31,26,46,.05)' }}>
                    <div style={{ height: 120, background: t.tint, position: 'relative', overflow: 'hidden' }}>
                      <img src={e.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      <div style={{ position: 'absolute', top: 10, left: 10 }}>
                        <EventTypePill type={e.type} size="sm" />
                      </div>
                    </div>
                    <div style={{ padding: '12px 14px 14px' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 6, lineHeight: 1.3 }}>{e.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', display: 'flex', gap: 10 }}>
                        <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}><Calendar size={10} color={t.color} /> {e.date}</span>
                        <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}><MapPin size={10} color="var(--muted)" /> {e.dist}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        ) : (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18, marginBottom: 16 }}>
          {PREVIEW_EVENTS.map(e => {
            const t = EVENT_TYPES[e.type as EventTypeKey]
            return (
              <Link key={e.id} to="/search" style={{ textDecoration: 'none' }}>
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid var(--line)',
                    borderRadius: 20,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    boxShadow: '0 1px 2px rgba(31,26,46,.04), 0 12px 28px rgba(31,26,46,.05)',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.02)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 2px rgba(31,26,46,.05), 0 18px 40px rgba(31,26,46,.08)' }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = ''; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 2px rgba(31,26,46,.04), 0 12px 28px rgba(31,26,46,.05)' }}
                >
                  {/* Photo */}
                  <div style={{ height: 140, background: t.tint, position: 'relative', overflow: 'hidden' }}>
                    <img src={e.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <div style={{ position: 'absolute', top: 12, left: 12 }}>
                      <EventTypePill type={e.type} size="sm" />
                    </div>
                    {e.recurring && (
                      <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,.85)', borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)' }}>
                        weekly
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div style={{ padding: '16px 20px 20px' }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', marginBottom: 8, lineHeight: 1.3, letterSpacing: '-0.01em' }}>
                      {e.name}
                    </div>
                    <div style={{ display: 'flex', gap: 14, fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 12 }}>
                      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <Calendar size={11} color={t.color} /> {e.date}
                      </span>
                      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <Clock size={11} color={t.color} /> {e.time}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <MapPin size={11} color="var(--muted)" />
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{e.dist} away</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: t.color }}>{e.going} going →</span>
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>

        {/* Compact row */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PREVIEW_EVENTS.map(e => {
            const t = EVENT_TYPES[e.type as EventTypeKey]
            return (
              <Link key={`compact-${e.id}`} to="/search" style={{ textDecoration: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', background: '#fff', border: '1px solid var(--line)', borderRadius: 14, cursor: 'pointer' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: t.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ width: 16, height: 16, borderRadius: '50%', background: t.color, display: 'inline-block' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 3, letterSpacing: '-0.01em' }}>{e.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', gap: 12 }}>
                      <span>{e.date} · {e.time}</span>
                      <span>{e.dist} away</span>
                    </div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: t.color, flexShrink: 0 }}>{e.going} going</span>
                  <ArrowRight size={14} color="var(--muted)" />
                </div>
              </Link>
            )
          })}
        </div>
        </>
        )}
      </section>

      {/* For-churches dark CTA */}
      <section style={{ maxWidth: 1240, margin: '0 auto', padding: isMobile ? '0 20px 48px' : '0 36px 100px' }}>
        <div style={{
          background: '#0a0c1f',
          borderRadius: isMobile ? 20 : 24,
          padding: isMobile ? '36px 24px' : '60px 64px',
          position: 'relative',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr',
          gap: isMobile ? 28 : 60,
          alignItems: 'center',
        }}>
          {/* Gradient blobs */}
          <div style={{ position: 'absolute', top: -80, right: 60, width: 340, height: 340, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,92,211,.35) 0%, transparent 65%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -60, left: 100, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,76,202,.30) 0%, transparent 65%)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: 'rgba(255,255,255,.45)', marginBottom: 16 }}>
              FOR CHURCHES
            </div>
            <h2 style={{ margin: '0 0 20px', fontSize: isMobile ? 28 : 40, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.025em', color: '#ffffff' }}>
              Reach beyond your own congregation.
            </h2>
            <p style={{ margin: '0 0 32px', fontSize: isMobile ? 15 : 17, lineHeight: 1.6, color: 'rgba(255,255,255,.6)' }}>
              Post events. People from any denomination can discover and attend. Build community across every tradition.
            </p>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
              <Link to="/churches/new" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '14px 28px', borderRadius: 999,
                background: GRADIENT, color: '#fff',
                fontSize: 15, fontWeight: 600, textDecoration: 'none',
                boxShadow: '0 4px 14px rgba(59,76,202,.5)',
              }}>
                Get started free <ArrowRight size={16} />
              </Link>
              <Link to="/churches" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '14px 24px', borderRadius: 999,
                border: '1.5px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.75)',
                fontSize: 15, fontWeight: 500, textDecoration: 'none',
              }}>
                See example churches
              </Link>
            </div>
          </div>

          {!isMobile && (
            <div style={{ position: 'relative' }}>
              <img
                src="https://images.unsplash.com/photo-1438232992991-995b7058bbb3?auto=format&fit=crop&w=800&h=600&q=80"
                alt=""
                style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 16, border: '1px solid rgba(255,255,255,.08)', display: 'block' }}
              />
            </div>
          )}
        </div>
      </section>

    </PublicLayout>
  )
}
